import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, 
  FileText,
  Trash2,
  Check,
  X,
  Euro,
  Send,
  Clock,
  Download,
  Share2,
  Eye,
  Printer,
  Mail,
  MessageCircle,
  Copy,
  Scissors,
  Package,
  Search,
  Filter,
  AlertTriangle,
  Phone
} from "lucide-react";

export default function Preventivi() {
  const { token } = useAuth();
  const [preventivi, setPreventivi] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPreventivo, setSelectedPreventivo] = useState(null);
  const [filterCategoria, setFilterCategoria] = useState("tutti");
  const [salonSettings, setSalonSettings] = useState(null);
  
  // Filtri e ricerca
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStato, setFilterStato] = useState("tutti");
  const [expiredCount, setExpiredCount] = useState(0);
  
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nome: "",
    cliente_telefono: "",
    cliente_email: "",
    servizi: [],
    prodotti: [],
    sconto: 0,
    note: "",
    validita_giorni: 30
  });

  const printRef = useRef(null);

  useEffect(() => {
    fetchData();
    // Pulizia automatica preventivi scaduti al caricamento
    cleanupExpiredPreventivi();
  }, []);

  const fetchData = async () => {
    try {
      const [prevRes, clientiRes, serviziRes, prodottiRes, settingsRes] = await Promise.all([
        axios.get(`${API}/preventivi`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/servizi`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/prodotti`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      // Conta preventivi scaduti (non accettati da più di 7 giorni)
      const now = new Date();
      const expired = prevRes.data.filter(p => {
        if (p.stato === 'accettato') return false;
        const created = new Date(p.created_at);
        const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
        return daysDiff > 7;
      });
      setExpiredCount(expired.length);
      
      setPreventivi(prevRes.data);
      setClienti(clientiRes.data);
      setServizi(serviziRes.data.filter(s => s.attivo !== false));
      setProdotti(prodottiRes.data);
      setSalonSettings(settingsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Pulizia automatica preventivi scaduti (non accettati da più di 7 giorni)
  const cleanupExpiredPreventivi = async () => {
    try {
      const response = await axios.delete(`${API}/preventivi/cleanup-expired`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.deleted > 0) {
        toast.info(`${response.data.deleted} preventivi scaduti eliminati automaticamente`);
      }
    } catch (error) {
      // Endpoint potrebbe non esistere ancora - log silenzioso
      console.debug("Auto-delete expired preventivi:", error?.message || "endpoint not available");
    }
  };

  // Elimina manualmente tutti i preventivi scaduti
  const deleteExpiredPreventivi = async () => {
    if (!window.confirm(`Eliminare ${expiredCount} preventivi scaduti (non accettati da più di 7 giorni)?`)) return;
    
    const now = new Date();
    const expiredIds = preventivi
      .filter(p => {
        if (p.stato === 'accettato') return false;
        const created = new Date(p.created_at);
        const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
        return daysDiff > 7;
      })
      .map(p => p.id);
    
    let deleted = 0;
    for (const id of expiredIds) {
      try {
        await axios.delete(`${API}/preventivi/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch (e) {
        console.error(`Errore eliminazione preventivo ${id}:`, e);
      }
    }
    
    toast.success(`${deleted} preventivi scaduti eliminati`);
    fetchData();
  };

  // Filtra preventivi in base a ricerca e stato
  const getFilteredPreventivi = () => {
    return preventivi.filter(p => {
      // Filtro per stato
      if (filterStato !== "tutti" && p.stato !== filterStato) return false;
      
      // Filtro per ricerca (nome cliente, servizi)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchNome = p.cliente_nome?.toLowerCase().includes(query);
        const matchServizi = p.servizi?.some(s => s.nome?.toLowerCase().includes(query));
        if (!matchNome && !matchServizi) return false;
      }
      
      return true;
    });
  };

  // Calcola giorni rimanenti alla scadenza
  const getDaysRemaining = (preventivo) => {
    const created = new Date(preventivo.created_at);
    const validita = preventivo.validita_giorni || 30;
    const scadenza = new Date(created.getTime() + validita * 24 * 60 * 60 * 1000);
    const now = new Date();
    const days = Math.ceil((scadenza - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Controlla se un preventivo è scaduto (più di 7 giorni)
  const isExpired = (preventivo) => {
    if (preventivo.stato === 'accettato') return false;
    const created = new Date(preventivo.created_at);
    const now = new Date();
    const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    return daysDiff > 7;
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente ? `${cliente.nome} ${cliente.cognome}` : "",
      cliente_telefono: cliente?.telefono || "",
      cliente_email: cliente?.email || ""
    });
  };

  const handleServizioToggle = (servizio) => {
    const servizioData = {
      id: servizio.id,
      nome: servizio.nome,
      prezzo: servizio.prezzo,
      durata_minuti: servizio.durata_minuti,
      categoria: servizio.categoria
    };
    
    const exists = formData.servizi.find(s => s.id === servizio.id);
    if (exists) {
      setFormData({
        ...formData,
        servizi: formData.servizi.filter(s => s.id !== servizio.id)
      });
    } else {
      setFormData({
        ...formData,
        servizi: [...formData.servizi, servizioData]
      });
    }
  };

  const handleProdottoToggle = (prodotto) => {
    const exists = formData.prodotti.find(p => p.prodotto_id === prodotto.id);
    if (exists) {
      setFormData({
        ...formData,
        prodotti: formData.prodotti.filter(p => p.prodotto_id !== prodotto.id)
      });
    } else {
      setFormData({
        ...formData,
        prodotti: [...formData.prodotti, {
          prodotto_id: prodotto.id,
          nome: prodotto.nome,
          quantita: 1,
          prezzo: prodotto.prezzo_vendita || prodotto.prezzo || 0
        }]
      });
    }
  };

  const updateProdottoQty = (prodottoId, qty) => {
    setFormData({
      ...formData,
      prodotti: formData.prodotti.map(p =>
        p.prodotto_id === prodottoId ? { ...p, quantita: Math.max(1, qty) } : p
      )
    });
  };

  const calcTotali = () => {
    const totServizi = formData.servizi.reduce((sum, s) => sum + s.prezzo, 0);
    const totProdotti = formData.prodotti.reduce((sum, p) => sum + (p.prezzo * p.quantita), 0);
    const subtotale = totServizi + totProdotti;
    const scontoValore = (subtotale * formData.sconto) / 100;
    const totale = subtotale - scontoValore;
    const durataTotale = formData.servizi.reduce((sum, s) => sum + (s.durata_minuti || 0), 0);
    
    return { totServizi, totProdotti, subtotale, scontoValore, totale, durataTotale };
  };

  const formatDurata = (minuti) => {
    if (!minuti || minuti === 0) return "0 min";
    const ore = Math.floor(minuti / 60);
    const min = minuti % 60;
    if (ore === 0) return `${min} min`;
    if (min === 0) return `${ore}h`;
    return `${ore}h ${min}min`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.servizi.length === 0 && formData.prodotti.length === 0) {
      toast.error("Seleziona almeno un servizio o prodotto");
      return;
    }
    
    const totali = calcTotali();
    const dataToSend = {
      ...formData,
      totale: totali.totale,
      subtotale: totali.subtotale,
      durata_totale: totali.durataTotale
    };
    
    try {
      await axios.post(`${API}/preventivi`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preventivo salvato con successo!");
      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleStatoChange = async (id, stato) => {
    try {
      await axios.put(`${API}/preventivi/${id}/stato?stato=${stato}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stato aggiornato");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo preventivo?")) return;
    try {
      await axios.delete(`${API}/preventivi/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preventivo eliminato");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nome: "",
      cliente_telefono: "",
      cliente_email: "",
      servizi: [],
      prodotti: [],
      sconto: 0,
      note: "",
      validita_giorni: 30
    });
    setFilterCategoria("tutti");
  };

  const viewPreventivo = (prev) => {
    setSelectedPreventivo(prev);
    setViewDialogOpen(true);
  };

  // Genera testo preventivo per condivisione
  const generatePreventivoText = (prev) => {
    const salonName = salonSettings?.nome_salone || "I CIRILLO PARRUCCHIERI";
    let text = `*${salonName}*\n`;
    text += `📋 PREVENTIVO\n`;
    text += `━━━━━━━━━━━━━━━\n\n`;
    text += `👤 Cliente: ${prev.cliente_nome}\n`;
    text += `📅 Data: ${new Date(prev.created_at).toLocaleDateString('it-IT')}\n\n`;
    
    if (prev.servizi?.length > 0) {
      text += `✂️ *SERVIZI*\n`;
      prev.servizi.forEach(s => {
        text += `• ${s.nome} - €${s.prezzo.toFixed(2)}`;
        if (s.durata_minuti) text += ` (${formatDurata(s.durata_minuti)})`;
        text += `\n`;
      });
      text += `\n`;
    }
    
    if (prev.prodotti?.length > 0) {
      text += `🛍️ *PRODOTTI*\n`;
      prev.prodotti.forEach(p => {
        text += `• ${p.nome} x${p.quantita} - €${(p.prezzo * p.quantita).toFixed(2)}\n`;
      });
      text += `\n`;
    }
    
    text += `━━━━━━━━━━━━━━━\n`;
    if (prev.sconto > 0) {
      text += `Subtotale: €${prev.subtotale?.toFixed(2) || prev.totale.toFixed(2)}\n`;
      text += `Sconto: -${prev.sconto}%\n`;
    }
    text += `*TOTALE: €${prev.totale.toFixed(2)}*\n`;
    if (prev.durata_totale) {
      text += `⏱️ Durata stimata: ${formatDurata(prev.durata_totale)}\n`;
    }
    
    if (prev.note) {
      text += `\n📝 Note: ${prev.note}\n`;
    }
    
    text += `\n_Preventivo valido ${prev.validita_giorni || 30} giorni_`;
    
    return text;
  };

  // Condividi via WhatsApp
  const shareWhatsApp = (prev) => {
    const text = generatePreventivoText(prev);
    const phone = prev.cliente_telefono?.replace(/\D/g, '') || '';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // Condividi via Email
  const shareEmail = (prev) => {
    const salonName = salonSettings?.nome_salone || "I CIRILLO PARRUCCHIERI";
    const subject = `Preventivo - ${salonName}`;
    const body = generatePreventivoText(prev).replace(/\*/g, '').replace(/━/g, '-');
    const url = `mailto:${prev.cliente_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  };

  // Copia negli appunti
  const copyToClipboard = (prev) => {
    const text = generatePreventivoText(prev).replace(/\*/g, '');
    navigator.clipboard.writeText(text);
    toast.success("Preventivo copiato negli appunti!");
  };

  // Stampa preventivo
  const printPreventivo = () => {
    window.print();
  };

  const filteredServizi = filterCategoria === "tutti" 
    ? servizi 
    : servizi.filter(s => s.categoria === filterCategoria);

  const totali = calcTotali();
  const filteredPreventivi = getFilteredPreventivi();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="preventivi">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-slate-500">Crea e gestisci preventivi per i tuoi clienti</p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700" 
          data-testid="new-preventivo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Preventivo
        </Button>
      </div>

      {/* Barra Ricerca e Filtri */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Ricerca */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per cliente o servizio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Filtro Stato */}
            <div className="flex gap-2">
              {[
                { value: "tutti", label: "Tutti", color: "bg-slate-100 text-slate-700" },
                { value: "in_attesa", label: "In Attesa", color: "bg-amber-100 text-amber-700" },
                { value: "accettato", label: "Accettati", color: "bg-emerald-100 text-emerald-700" },
                { value: "rifiutato", label: "Rifiutati", color: "bg-red-100 text-red-700" }
              ].map(stato => (
                <button
                  key={stato.value}
                  onClick={() => setFilterStato(stato.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filterStato === stato.value 
                      ? `${stato.color} ring-2 ring-offset-1 ring-blue-400` 
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {stato.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Info e azioni rapide */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">
                {filteredPreventivi.length} preventiv{filteredPreventivi.length === 1 ? 'o' : 'i'} 
                {filterStato !== "tutti" || searchQuery ? " trovati" : " totali"}
              </span>
              {expiredCount > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  {expiredCount} scadut{expiredCount === 1 ? 'o' : 'i'} (7+ giorni)
                </span>
              )}
            </div>
            
            {expiredCount > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={deleteExpiredPreventivi}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina Scaduti
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preventivi List */}
      {filteredPreventivi.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredPreventivi.map((prev, index) => {
            const expired = isExpired(prev);
            const daysRemaining = getDaysRemaining(prev);
            
            return (
            <Card 
              key={prev.id} 
              className={`border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer ${
                expired ? 'opacity-60 border-l-4 border-l-red-400' : ''
              }`}
              onClick={() => viewPreventivo(prev)}
              data-testid={`preventivo-${prev.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-800 text-lg">{prev.cliente_nome}</h3>
                    <p className="text-sm text-slate-400">
                      {new Date(prev.created_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    {prev.stato === 'in_attesa' && !expired && daysRemaining <= 3 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Scade tra {daysRemaining} giorn{daysRemaining === 1 ? 'o' : 'i'}
                      </p>
                    )}
                    {expired && (
                      <p className="text-xs text-red-500 mt-1 font-medium">
                        Scaduto - Da eliminare
                      </p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    prev.stato === 'accettato' ? 'bg-emerald-100 text-emerald-700' :
                    prev.stato === 'rifiutato' ? 'bg-red-100 text-red-700' : 
                    expired ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {expired ? 'Scaduto' :
                     prev.stato === 'in_attesa' ? 'In Attesa' : 
                     prev.stato === 'accettato' ? 'Accettato' : 'Rifiutato'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  {prev.servizi?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Scissors className="w-4 h-4 text-pink-500 mt-0.5" />
                      <p className="text-sm text-slate-600">
                        {prev.servizi.map(s => s.nome).join(', ')}
                      </p>
                    </div>
                  )}
                  {prev.prodotti?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-purple-500 mt-0.5" />
                      <p className="text-sm text-slate-600">
                        {prev.prodotti.map(p => `${p.nome} x${p.quantita}`).join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Euro className="w-5 h-5 text-blue-600" />
                      <span className="text-xl font-bold text-blue-600">
                        {prev.totale.toFixed(2)}
                      </span>
                    </div>
                    {prev.durata_totale > 0 && (
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{formatDurata(prev.durata_totale)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 hover:bg-green-50"
                      onClick={() => shareWhatsApp(prev)}
                      title="Invia WhatsApp"
                    >
                      <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shareEmail(prev)}
                      title="Invia Email"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDelete(prev.id)}
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              {searchQuery || filterStato !== "tutti" ? "Nessun risultato" : "Nessun preventivo"}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery || filterStato !== "tutti" 
                ? "Prova a modificare i filtri di ricerca" 
                : "Crea il tuo primo preventivo per un cliente"}
            </p>
            {!searchQuery && filterStato === "tutti" && (
              <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Crea Preventivo
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Nuovo Preventivo */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nuovo Preventivo
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Cliente */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Cliente</Label>
              <Select value={formData.cliente_id} onValueChange={handleClienteChange}>
                <SelectTrigger data-testid="select-cliente">
                  <SelectValue placeholder="Seleziona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clienti.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome} {c.cognome} {c.telefono && `- ${c.telefono}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Servizi */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Scissors className="w-4 h-4 text-pink-500" />
                  Servizi
                </Label>
                <div className="flex gap-1">
                  {["tutti", "donna", "uomo", "unisex"].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFilterCategoria(cat)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        filterCategoria === cat 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat === 'tutti' ? 'Tutti' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-3 bg-slate-50 rounded-lg">
                {filteredServizi.map(servizio => {
                  const isSelected = formData.servizi.some(s => s.id === servizio.id);
                  return (
                    <div
                      key={servizio.id}
                      onClick={() => handleServizioToggle(servizio)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-blue-100 border-2 border-blue-400 shadow-sm'
                          : 'bg-white border border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{servizio.nome}</p>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-blue-600 font-semibold">€{servizio.prezzo.toFixed(2)}</span>
                          <span className="text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {servizio.durata_minuti} min
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Prodotti */}
            {prodotti.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Package className="w-4 h-4 text-purple-500" />
                  Prodotti (opzionale)
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-3 bg-slate-50 rounded-lg">
                  {prodotti.map(prodotto => {
                    const selected = formData.prodotti.find(p => p.prodotto_id === prodotto.id);
                    return (
                      <div
                        key={prodotto.id}
                        onClick={() => handleProdottoToggle(prodotto)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selected
                            ? 'bg-purple-100 border-2 border-purple-400'
                            : 'bg-white border border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selected ? 'bg-purple-600 border-purple-600' : 'border-slate-300'
                          }`}>
                            {selected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{prodotto.nome}</p>
                            <p className="text-purple-600 font-semibold text-sm">
                              €{(prodotto.prezzo_vendita || prodotto.prezzo || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        {selected && (
                          <div className="flex items-center gap-2 mt-2 ml-7" onClick={(e) => e.stopPropagation()}>
                            <Label className="text-xs text-slate-500">Qtà:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={selected.quantita}
                              onChange={(e) => updateProdottoQty(prodotto.id, parseInt(e.target.value) || 1)}
                              className="h-8 w-20 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sconto e Note */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sconto %</Label>
                <Select 
                  value={formData.sconto.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, sconto: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nessuno sconto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nessuno sconto</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="15">15%</SelectItem>
                    <SelectItem value="20">20%</SelectItem>
                    <SelectItem value="25">25%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Validità (giorni)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.validita_giorni}
                  onChange={(e) => setFormData({ ...formData, validita_giorni: parseInt(e.target.value) || 30 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Note aggiuntive per il cliente..."
                rows={3}
              />
            </div>

            {/* Riepilogo */}
            {(formData.servizi.length > 0 || formData.prodotti.length > 0) && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl space-y-3">
                <h4 className="font-semibold text-slate-800">Riepilogo Preventivo</h4>
                
                {formData.servizi.length > 0 && (
                  <div className="space-y-1">
                    {formData.servizi.map(s => (
                      <div key={s.id} className="flex justify-between text-sm">
                        <span className="text-slate-600">{s.nome}</span>
                        <span className="font-medium">€{s.prezzo.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {formData.prodotti.length > 0 && (
                  <div className="space-y-1 pt-2 border-t border-blue-200">
                    {formData.prodotti.map(p => (
                      <div key={p.prodotto_id} className="flex justify-between text-sm">
                        <span className="text-slate-600">{p.nome} x{p.quantita}</span>
                        <span className="font-medium">€{(p.prezzo * p.quantita).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="pt-3 border-t border-blue-200 space-y-2">
                  {formData.sconto > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Subtotale</span>
                        <span>€{totali.subtotale.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Sconto ({formData.sconto}%)</span>
                        <span>-€{totali.scontoValore.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-slate-700">TOTALE</span>
                    <span className="text-2xl font-bold text-blue-600">€{totali.totale.toFixed(2)}</span>
                  </div>
                  
                  {totali.durataTotale > 0 && (
                    <div className="flex justify-between items-center text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Durata stimata
                      </span>
                      <span className="font-medium">{formatDurata(totali.durataTotale)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bottoni */}
            <div className="flex gap-3">
              <Button 
                type="submit" 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={!formData.cliente_id || (formData.servizi.length === 0 && formData.prodotti.length === 0)}
              >
                <Check className="w-4 h-4 mr-2" />
                Salva Preventivo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizza Preventivo */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span style={{ fontFamily: 'Playfair Display, serif' }}>Dettaglio Preventivo</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                selectedPreventivo?.stato === 'accettato' ? 'bg-emerald-100 text-emerald-700' :
                selectedPreventivo?.stato === 'rifiutato' ? 'bg-red-100 text-red-700' : 
                'bg-amber-100 text-amber-700'
              }`}>
                {selectedPreventivo?.stato === 'in_attesa' ? 'In Attesa' : 
                 selectedPreventivo?.stato === 'accettato' ? 'Accettato' : 'Rifiutato'}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedPreventivo && (
            <div className="space-y-4" ref={printRef}>
              {/* Info Cliente */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-lg text-slate-800">{selectedPreventivo.cliente_nome}</h4>
                <p className="text-sm text-slate-500">
                  Creato il {new Date(selectedPreventivo.created_at).toLocaleDateString('it-IT', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </p>
              </div>

              {/* Servizi */}
              {selectedPreventivo.servizi?.length > 0 && (
                <div>
                  <h5 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Scissors className="w-4 h-4 text-pink-500" />
                    Servizi
                  </h5>
                  <div className="space-y-2">
                    {selectedPreventivo.servizi.map((s) => (
                      <div key={s.id || s.nome} className="flex justify-between p-2 bg-pink-50 rounded">
                        <span>{s.nome}</span>
                        <span className="font-medium">€{s.prezzo.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prodotti */}
              {selectedPreventivo.prodotti?.length > 0 && (
                <div>
                  <h5 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4 text-purple-500" />
                    Prodotti
                  </h5>
                  <div className="space-y-2">
                    {selectedPreventivo.prodotti.map((p) => (
                      <div key={p.id || p.nome} className="flex justify-between p-2 bg-purple-50 rounded">
                        <span>{p.nome} x{p.quantita}</span>
                        <span className="font-medium">€{(p.prezzo * p.quantita).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totale */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-700">TOTALE</span>
                  <span className="text-2xl font-bold text-blue-600">€{selectedPreventivo.totale.toFixed(2)}</span>
                </div>
                {selectedPreventivo.durata_totale > 0 && (
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Durata: {formatDurata(selectedPreventivo.durata_totale)}
                  </p>
                )}
              </div>

              {/* Note */}
              {selectedPreventivo.note && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-slate-600">{selectedPreventivo.note}</p>
                </div>
              )}

              {/* Azioni */}
              <div className="space-y-3 pt-4 border-t">
                {/* Stato */}
                {selectedPreventivo.stato === 'in_attesa' && (
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        handleStatoChange(selectedPreventivo.id, 'accettato');
                        setViewDialogOpen(false);
                      }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accetta
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        handleStatoChange(selectedPreventivo.id, 'rifiutato');
                        setViewDialogOpen(false);
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rifiuta
                    </Button>
                  </div>
                )}

                {/* Condivisione */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => shareWhatsApp(selectedPreventivo)}
                  >
                    <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => shareEmail(selectedPreventivo)}
                  >
                    <Mail className="w-4 h-4 mr-2 text-blue-600" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(selectedPreventivo)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
